import { Box } from '@material-ui/core';
import { lighten, makeStyles, Theme } from '@material-ui/core/styles';
import { LoginLogoImg } from '@roc/feature-app-core';
import {
  US_TREASURY_10_YR_DESC,
  US_TREASURY_10_YR_INDEX,
  US_TREASURY_5_YR_DESC,
  US_TREASURY_5_YR_INDEX,
} from '@roc/feature-loans';
import {
  formatDate as _formatDate,
  formatBorrowerExperience,
  formatToLocalDateTimeString,
  borrowerExpMapping,
  LoanStatus,
} from '@roc/feature-utils';
import { PrintableArea } from '@roc/ui';
import {
  formatCurrency as _formatCurrency,
  formatPercentage as _formatPercentage,
} from '@roc/ui/utils';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { forwardRef } from 'react';

const AGREED_OR_LATER_LOAN_STATUSES: string[] = [
  LoanStatus.AGREED,
  LoanStatus.FUNDED,
  LoanStatus.PAID_OFF,
  LoanStatus.PAID_OFF_BUYOUT,
];

const getSwapIndexLabel = (index: number) => {
  if (index === US_TREASURY_5_YR_INDEX) return US_TREASURY_5_YR_DESC;
  else if (index === US_TREASURY_10_YR_INDEX) return US_TREASURY_10_YR_DESC;
};

interface TheConditionalTermsWeCanOfferProps {
  data: any;
  loan: any;
}
const TheConditionalTermsWeCanOffer = ({
  data,
  loan,
}: TheConditionalTermsWeCanOfferProps) => {
  const classes = useStyles();

  const buydownFee = parseFloat(data?.buydownFee || 0);
  const loanAmount = parseFloat(loan?.amount) || 0;
  const buydownPoints =
    buydownFee && loanAmount ? (buydownFee / loanAmount) * 100 : 0;
  return (
    <>
      <div className={classes.section}>Compare Pricing Options:</div>
      <table
        className={clsx(
          classes.table,
          classes.quotesTable ? classes.quotesTable : undefined
        )}
      >
        <tbody>
          <tr>
            <td>LTV</td>
            <td>
              <Box>{formatPercentage(data?.ltv)}</Box>
            </td>
          </tr>
          <tr>
            <td>Loan Amount</td>
            <td>
              <Box>{data?.totalLoanAmount}</Box>
            </td>
          </tr>
          <tr>
            <td>Loan Purpose</td>
            <td>
              <Box>{data?.loanPurpose}</Box>
            </td>
          </tr>
          <tr>
            <td>Amortization</td>
            <td>
              <Box>{data?.amortization}</Box>
            </td>
          </tr>
          <tr>
            <td>Pre-Payment Penalty</td>
            <td>
              <Box>{data?.prePaymentPenalty}</Box>
            </td>
          </tr>
          <tr>
            <td>Rate Type</td>
            <td>
              <Box>{data?.rateType}</Box>
            </td>
          </tr>
          <tr>
            <td>Index</td>
            <td>
              <Box>{getSwapIndexLabel(loan?.loanPricingData?.swapIndex)}</Box>
            </td>
          </tr>
          <tr>
            <td>Spread Over Index</td>
            <td>
              <Box>{formatPercentage(data?.spreadRate)}</Box>
            </td>
          </tr>
          <tr>
            <td>Today's Gross Rate</td>
            <td>
              <Box>{formatPercentage(data?.todaysGrossRate)}</Box>
            </td>
          </tr>
          <tr>
            <td>Monthly Mortgage Payment (Principal and Interest Payments)</td>
            <td>{formatCurrency(data?.properties[0]?.monthlyDebtService)}</td>
          </tr>
          <tr>
            <td>PITIA (Principal, Interest, Taxes, Insurance)</td>
            <td>
              <Box>{formatCurrency(data?.pittia) ?? '-'}</Box>
            </td>
          </tr>
          <tr>
            <td>Post Loan Approval Rate Lock</td>
            <td>
              <Box>14 Calendar Days</Box>
            </td>
          </tr>
          {!!data?.brokerOriginationPoints && (
            <tr>
              <td>Broker Fee</td>
              <td>
                <Box>{formatPercentage(data?.brokerOriginationPoints)}</Box>
              </td>
            </tr>
          )}
          {!!data?.originationPoints && (
            <tr>
              <td> Origination Points</td>
              <td>
                <Box>{formatPercentage(data?.originationPoints)}</Box>
              </td>
            </tr>
          )}
          {!!data?.processingFee && (
            <tr>
              <td>Processing Fees</td>
              <td>
                <Box>{formatCurrency(data?.processingFee)}</Box>
              </td>
            </tr>
          )}
          {!!data?.adminFee && (
            <tr>
              <td>Admin Fees</td>
              <td>
                <Box>{formatCurrency(data?.adminFee)}</Box>
              </td>
            </tr>
          )}
          {!!data?.underwritingFee && (
            <tr>
              <td>Underwriting Fees</td>
              <td>
                <Box>{formatCurrency(data?.underwritingFee)}</Box>
              </td>
            </tr>
          )}
          {!!data?.commitmentFee && (
            <tr>
              <td>Commitment Fees</td>
              <td>
                <Box>{formatCurrency(data?.commitmentFee)}</Box>
              </td>
            </tr>
          )}
          {!!buydownPoints && (
            <tr>
              <td>Buydown Points</td>
              <td>
                <Box>{formatPercentage(buydownPoints)}</Box>
              </td>
            </tr>
          )}
          {!!data?.corelogixFee && (
            <tr>
              <td>Corelogix Fee</td>
              <td>
                <Box>{formatCurrency(data?.corelogixFee)}</Box>
              </td>
            </tr>
          )}
          {!!data?.legalReviewFee && (
            <tr>
              <td>Legal Review Fee</td>
              <td>
                <Box>{formatCurrency(data?.legalReviewFee)}</Box>
              </td>
            </tr>
          )}
          {!!data?.dscr && (
            <tr>
              <td>DSCR (Debt Service Coverage Ratio)</td>
              <td>
                <Box>{data.dscr}</Box>
              </td>
            </tr>
          )}
          <tr>
            <td>Liquidity Requirement</td>
            <td>
              <Box>{formatCurrency(loan?.liquidityNeededForClose)}</Box>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

interface IndexAndRateLockInformationProps {
  data: any;
  loan: any;
}
const IndexAndRateLockInformation = ({
  data,
  loan,
}: IndexAndRateLockInformationProps) => {
  const classes = useStyles();
  return (
    <>
      <div className={classes.section}>Loan Information:</div>
      <table className={classes.table}>
        <tbody>
          {data?.preAgreedLoan && (
            <tr className={classes.thickRow}>
              <td style={{ width: '25%' }}>
                <b>
                  Index Rates as of {formatDate(new Date())}
                </b>
              </td>
              <td>
                5 Yr US Treasury: <b>{data?.fiveYrTsrySwap}%</b>
                <br />
                10 Yr US Treasury: <b>{data?.tenYrTsrySwap}%</b>
              </td>
            </tr>
          )}
          {!data?.preAgreedLoan && (
            <tr className={classes.thickRow}>
              <td style={{ width: '25%' }}>
                <b>Index rate as of price lock date</b>
              </td>
              <td>
                {getSwapIndexLabel(loan?.loanPricingData?.swapIndex)}{' '}
                <b>{data?.agreedSwapRate}%</b>
              </td>
            </tr>
          )}
          <tr>
            <td colSpan={2}>
              <ul>
                <li>
                  Indicative Gross Rates are calculated as the sum of (1) value
                  of the Index today + (2) Spread over Index.
                  <b>
                    <u>
                      They represent today's rates, based on the information
                      submitted at pricing.
                    </u>
                  </b>
                </li>
                <li>
                  Gross rates are determined at the time of credit approval,
                  calculated as the sum of the (1) value of the Index as of the
                  date of credit approval, and (2) Spread over Index.
                </li>
                <li>
                  50% of cash out can be used to satisfy liquidity requirement
                </li>
              </ul>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

interface MonthlyExpensesBreakDownProps {
  data: any;
}
const MonthlyExpensesBreakDown = ({ data }: MonthlyExpensesBreakDownProps) => {
  const classes = useStyles();
  return (
    <>
      <div className={classes.section}>Monthly Expenses Breakdown:</div>
      <table
        className={clsx(
          classes.table,
          classes.fullWidth,
          classes.quotesTable ? classes.quotesTable : undefined
        )}
      >
        <tbody>
          <tr>
            <td className={classes.labelColumn}>
              <strong>Item</strong>
            </td>
            <td>
              <strong>Amount</strong>
            </td>
          </tr>
          <tr>
            <td>Property Taxes </td>
            <td>
              <Box>{formatCurrency(data?.properties[0]?.monthlyTaxes)}</Box>
            </td>
          </tr>
          <tr>
            <td>Insurance</td>
            <td>
              <Box>{formatCurrency(data?.properties[0]?.monthlyInsurance)}</Box>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

const formatCurrency = (value: any) => (value ? _formatCurrency(value) : 'N/A');
const formatPercentage = (value: any) =>
  value ? _formatPercentage(value, 4) : 'N/A';
const formatDate = (value: any) =>
  value ? _formatDate(value, 'MMMM d, yyyy') : 'N/A';

const useStyles = makeStyles((theme: Theme) => ({
  page: {
    color: theme.palette.text.primary,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'verdana, arial, sans-serif',
    lineHeight: '1.2',
    fontSize: '9.5pt',
    position: 'relative',

    ['& p']: {
      margin: '1em 0',
    },
    ['& li']: {
      margin: '0.5em 0',
      marginLeft: '2em',
    },
    ['& sup']: {
      fontSize: '80%',
      verticalAlign: 'super',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  table: {
    borderCollapse: 'collapse',
    ['& td']: {
      borderCollapse: 'collapse',
      padding: '5pt',
      border: '0.25pt solid ' + lighten(theme.palette.primary.dark, 0.5),
    },
    ['& th']: {
      color: 'black',
      textAlign: 'center',
      padding: '12px 0px',
    },
    ['& td:first-child']: {
      borderLeft: 0,
    },
    ['& td:last-child']: {
      borderRight: 0,
    },
  },
  thickRow: {
    borderTop: '2pt solid ' + lighten(theme.palette.primary.dark, 0.3),
  },
  header: {
    paddingBottom: '0.2in',
    width: '100%',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  promoSubtitle: {
    fontSize: '16px',
    fontWeight: 700,
  },
  promoTitle: {
    color: '#285391',
    fontSize: '20px',
    fontWeight: 700,
    marginLeft: '10px',
  },
  promoBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(2, 136, 209, 0.08)',
    marginTop: '16px',
    padding: '16px',
  },
  headerDateTime: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    fontSize: '10pt',
    color: theme.palette.text.secondary,
    whiteSpace: 'pre-line',
    minWidth: '90px',
    marginLeft: '24px',
  },
  protoTitleContainer: {
    display: 'flex',
  },
  logoContainer: {
    margin: 0,
    marginRight: '20px',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
  headerLine: {
    borderBottom: '4px solid ' + theme.palette.primary.main,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    paddingLeft: '0',
    paddingRight: '0',
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    justfyContent: 'flex-end',
    paddingTop: '0.2in',
  },
  title: {
    fontSize: '30pt',
    paddingBottom: '2px',
  },
  subtitle: {
    fontSize: '23pt',
    paddingBottom: '12px',
  },
  bold: {
    fontWeight: 'bold',
  },
  center: {
    textAlign: 'center',
  },
  darkBackground: {
    backgroundColor: theme.palette.primary.light,
    fontWeight: 'bold',
    textAlign: 'right',
    color: 'black',
  },
  note: {
    fontSize: '6pt',
    color: 'rgb(102 102 102)',
  },
  section: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    padding: '4pt 0',
    marginBottom: '12pt',
    fontSize: '15pt',
  },
  showOnPrint: {
    display: 'none',
  },
  pdfOnlyPage: {
    position: 'absolute',
    left: '-10000px',
    top: 0,
    width: '8.3in',
  },
  hideOnPrint: {
    display: 'block',
  },
  quotesTable: {
    ['& tr td']: {
      textAlign: 'center',
    },
    ['& tr td:first-child']: {
      fontWeight: 'bold',
      textAlign: 'left',
    },
  },
  fullWidth: {
    width: '100%',
  },
  labelColumn: {
    width: '25%',
  },
  informationSharedWithUsTable: {
    ['& td']: {
      width: '25%',
    },
  },
  warnings: {
    color: theme.palette.error.main,
  },
  date: {
    fontSize: '12pt',
    textAlign: 'right',
    '& div': {
      fontWeight: '700',
      paddingBottom: '8px',
    },
  },
  print: {
    //A4 size page
    width: '8.3in',
    height: '11.7in',

    //Add paddings
    padding: '0 0.65in',
    ['& $header']: {
      paddingTop: '0.6in',
    },

    ['& $footer']: {
      paddingBottom: '0.6in',
    },

    // Display hidden elements
    ['& $showOnPrint']: {
      display: 'flex',
    },
    ['& $pdfOnlyPage']: {
      position: 'relative',
      left: 'auto',
      top: 'auto',
      width: 'auto',
    },
    ['& $hideOnPrint']: {
      display: 'none',
    },
  },
  freeAppraisalText: {
    marginTop: '10px',
  },
}));

export const TermSheetRentalHTML = observer(
  forwardRef<any, { data: any; loan: any }>((props, ref) => {
    const { data, loan } = props;
    const classes = useStyles();
    const documentLabel = AGREED_OR_LATER_LOAN_STATUSES.includes(loan?.status)
      ? 'Term Sheet'
      : 'Quote';
    const now = new Date();
    const timeString = now.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });

    return (
      <PrintableArea printClass={classes.print} ref={ref}>
        {/* Page 1 */}
        <div className={classes.page}>
          <div className={classes.header}>
            <div className={classes.headerLine}>
              <div className={classes.headerLeft}>
                <div className={classes.logoContainer}>
                  <LoginLogoImg
                    containerClassName={classes.logoContainer}
                    imgStyle={{ height: '0.95in' }}
                  />
                </div>
                <div>
                  <div className={classes.title}>
                    Conditional <span className={classes.bold}>{documentLabel}</span>
                  </div>
                  <div className={classes.subtitle}>Rental Loan Program</div>
                </div>
              </div>
              <div className={classes.date}>
                <div>{formatToLocalDateTimeString(now, 'MM/dd/yyyy')}</div>
                <div>{timeString}</div>
              </div>
            </div>
          </div>
          <div className={classes.content}>
            <div className={classes.section}>Information Shared With Us:</div>
            <table
              className={clsx(
                classes.table,
                classes.informationSharedWithUsTable
              )}
            >
              <tbody>
                <tr>
                  <td className={classes.bold}>Property Address:</td>
                  <td colSpan={3}>{data?.properties[0]?.address}</td>
                </tr>
                <tr>
                  <td className={classes.bold}>Estimated As-Is Value*:</td>
                  <td>{data?.properties[0]?.asIsValue}</td>
                  <td className={classes.bold}>Property County:</td>
                  <td>{data?.properties[0]?.propertyCounty}</td>
                </tr>
                <tr>
                  <td className={classes.bold}>Property Type:</td>
                  <td>{data?.properties[0]?.propertyType}</td>
                  <td className={classes.bold}>Acquisition Date:</td>
                  <td>{formatDate(data?.properties[0]?.purchaseDate)}</td>
                </tr>
                <tr>
                  <td className={classes.bold}>Total Acquisition Cost:</td>
                  <td>{data?.properties[0]?.purchasePrice}</td>
                  <td className={classes.bold}>Completed Renovations:</td>
                  <td>{data?.properties[0]?.completedRenoToDate ?? 'N/A'}</td>
                </tr>
                <tr>
                  <td className={classes.bold}>Monthly Expenses:</td>
                  <td>
                    {formatCurrency(data?.properties[0]?.monthlyExpenses)}
                  </td>
                  <td className={classes.bold}>Monthly Rent*:</td>
                  <td>{formatCurrency(data?.properties[0]?.monthlyRent)}</td>
                </tr>
                <tr>
                  <td className={classes.bold}>Borrower’s Experience:</td>
                  <td>
                    {formatBorrowerExperience(
                      borrowerExpMapping[data?.borrowers[0]?.experienceScore] ??
                        data?.borrowers[0]?.experienceScore
                    )}
                  </td>
                  <td className={classes.bold}>Estimated Credit Score:</td>
                  <td>
                    {data?.borrowers?.map((borrower) => borrower?.ficoScore)
                      .filter((experience: any) => experience != null)
                      .join(', ')}
                  </td>
                </tr>
                <tr>
                  <td className={classes.bold}>Citizenship Status:</td>
                  <td>{data?.borrowers[0]?.citizenshipStatus}</td>
                  <td className={classes.bold}>Payoff/Debt:</td>
                  <td>{formatCurrency(data?.totalDebtPayoff)}</td>
                </tr>

              </tbody>
            </table>
            <div className={classes.note}>
              * Final As-Is and market rent values will be determined by
              third-party valuation reports satisfactory to Lender
            </div>
          </div>
          <div className={`${classes.footer} ${classes.showOnPrint}`}>
            <p className={classes.note}>
              Please note this letter of interest serves to outline the terms of
              the proposed financing of the referenced transaction. This letter
              is merely a general proposal, and is neither a binding offer, nor
              a contract. Borrower understands that no such offer will be
              forthcoming prior to completion of appropriate due diligence and
              underwriting performed and/or contracted by Lender. This proposal
              does not create any legally binding obligations on any party
              hereto. All properties are subject to satisfactory Lender due
              diligence underwriting including: satisfactory appraisal,
              satisfactory credit review of the borrowing entity and Key
              Principals, and satisfactory review of the property’s market and
              submarket.
            </p>
          </div>
        </div>
        {/* Page 2 */}
        <div className={classes.page}>
          <div className={`${classes.header} ${classes.showOnPrint}`}>
            <div className={classes.headerLine}>
              <div className={classes.headerLeft}>
                <div className={classes.logoContainer}>
                  <LoginLogoImg
                    containerClassName={classes.logoContainer}
                    imgStyle={{ height: '0.95in' }}
                  />
                </div>
                <div>
                  <div className={classes.title}>
                    Conditional <span className={classes.bold}>{documentLabel}</span>
                  </div>
                  <div className={classes.subtitle}>Rental Loan Program</div>
                </div>
              </div>
              <div className={classes.date}>
                <div>{formatToLocalDateTimeString(now, 'MM/dd/yyyy')}</div>
                <div>{timeString}</div>
              </div>
            </div>
          </div>
          <div className={classes.content}>
            <TheConditionalTermsWeCanOffer data={data} loan={loan} />
            <div className={classes.hideOnPrint}>
              <MonthlyExpensesBreakDown data={data} />
            </div>
            <div className={classes.hideOnPrint}>
              <IndexAndRateLockInformation data={data} loan={loan} />
            </div>
          </div>
          <div className={classes.footer}>
            <p className={classes.note}>
              Please note this letter of interest serves to outline the terms of
              the proposed financing of the referenced transaction. This letter
              is merely a general proposal, and is neither a binding offer, nor
              a contract. Borrower understands that no such offer will be
              forthcoming prior to completion of appropriate due diligence and
              underwriting performed and/or contracted by Lender. This proposal
              does not create any legally binding obligations on any party
              hereto. All properties are subject to satisfactory Lender due
              diligence underwriting including: satisfactory appraisal,
              satisfactory credit review of the borrowing entity and Key
              Principals, and satisfactory review of the property’s market and
              submarket.
            </p>
          </div>
        </div>
        {/* Page 3 - PDF only */}
        <div className={`${classes.page} ${classes.pdfOnlyPage}`}>
          <div className={classes.header}>
            <div className={classes.headerLine}>
              <div className={classes.headerLeft}>
                <div className={classes.logoContainer}>
                  <LoginLogoImg
                    containerClassName={classes.logoContainer}
                    imgStyle={{ height: '0.95in' }}
                  />
                </div>
                <div>
                  <div className={classes.title}>
                    Conditional <span className={classes.bold}>{documentLabel}</span>
                  </div>
                  <div className={classes.subtitle}>Rental Loan Program</div>
                </div>
              </div>
              <div className={classes.date}>
                <div>{formatToLocalDateTimeString(now, 'MM/dd/yyyy')}</div>
                <div>{timeString}</div>
              </div>
            </div>
          </div>
          <div className={classes.content}>
            <MonthlyExpensesBreakDown data={data} />
            <br />
            <IndexAndRateLockInformation data={data} loan={loan} />
          </div>
          <div className={classes.footer}>
            <p className={classes.note}>
              Please note this letter of interest serves to outline the terms of
              the proposed financing of the referenced transaction. This letter
              is merely a general proposal, and is neither a binding offer, nor
              a contract. Borrower understands that no such offer will be
              forthcoming prior to completion of appropriate due diligence and
              underwriting performed and/or contracted by Lender. This proposal
              does not create any legally binding obligations on any party
              hereto. All properties are subject to satisfactory Lender due
              diligence underwriting including: satisfactory appraisal,
              satisfactory credit review of the borrowing entity and Key
              Principals, and satisfactory review of the property’s market and
              submarket.
            </p>
          </div>
        </div>
      </PrintableArea>
    );
  })
);

export default TermSheetRentalHTML;
